import { createSlice } from '@reduxjs/toolkit';
import FetchStatus from '../../../Data/FetchStatus';
import {
  firstPosition,
  nextPosition,
  prevPosition,
  findSolarStepKey,
} from '../../../Helpers/clientApiJourneyPosition.helpers';

export const clientApiJourney = createSlice({
  name: 'CLIENT_API_JOURNEY',
  initialState: {}, // comes from preloadedState in the store

  reducers: {
    setLocationsStatus(state, action) {
      return {
        ...state,
        location: {
          ...state.location,
          listStatus: action.payload?.locationsStatus,
        },
      };
    },
    setSolarLoadStatus(state, action) {
      return {
        ...state,
        solar: { ...state.solar, loadStatus: action.payload?.solarLoadStatus },
      };
    },
    setSolarVerifyStatus(state, action) {
      return {
        ...state,
        solar: {
          ...state.solar,
          verifyStatus: action.payload?.solarVerifyStatus,
        },
      };
    },
    setSolarLocationOverride(state, action) {
      return {
        ...state,
        solar: {
          ...state.solar,
          locationOverride: action.payload?.locationOverride,
        },
      };
    },
    resetSolar(state) {
      const solarStepKey = findSolarStepKey(state.screenSequence);
      const next = {
        ...state,
        solar: {
          loadStatus: FetchStatus.NOT_STARTED,
          verifyStatus: FetchStatus.NOT_STARTED,
        },
      };
      if (solarStepKey && state.stepStates?.[solarStepKey]) {
        next.stepStates = { ...state.stepStates };
        delete next.stepStates[solarStepKey];
      }
      return next;
    },
    setLocations(state, action) {
      return {
        ...state,
        location: {
          ...state.location,
          list: action.payload?.locations,
        },
      };
    },
    setSelectedLocation(state, action) {
      return {
        ...state,
        location: {
          ...state.location,
          selected: action.payload?.selectedLocation,
        },
      };
    },
    setSlotsStatus(state, action) {
      const { locationId, slotsStatus } = action.payload ?? {};
      return {
        ...state,
        location: {
          ...state.location,
          slotsByLocation: {
            ...state.location?.slotsByLocation,
            [locationId]: {
              ...state.location?.slotsByLocation?.[locationId],
              slotsStatus,
            },
          },
        },
      };
    },
    setSlots(state, action) {
      const { locationId, slots } = action.payload ?? {};
      return {
        ...state,
        location: {
          ...state.location,
          slotsByLocation: {
            ...state.location?.slotsByLocation,
            [locationId]: {
              ...state.location?.slotsByLocation?.[locationId],
              slots,
            },
          },
        },
      };
    },
    setSelectedSlot(state, action) {
      return {
        ...state,
        appointment: {
          ...state.appointment,
          selectedSlot: action.payload?.selectedSlot,
        },
      };
    },
    // The dag's step order, the screens it flattens to, and where in them the
    // customer is. Starting a journey drops anything a previous run left.
    initialiseJourney(state, action) {
      const { stepOrder = [], screenSequence = [] } = action.payload ?? {};
      return {
        ...state,
        stepOrder,
        screenSequence,
        position: firstPosition(screenSequence),
        stepStates: {},
        stepResponses: {},
        isFallingThrough: false,
      };
    },
    goToNextScreen(state) {
      return {
        ...state,
        position: nextPosition(state.screenSequence, state.position),
      };
    },
    goToPreviousScreen(state) {
      return {
        ...state,
        position: prevPosition(state.screenSequence, state.position),
      };
    },
    // What a step produced (the dag's `state`), read back by later steps'
    // dependency paths.
    setStepState(state, action) {
      const { stepKey, state: stepState } = action.payload ?? {};
      return {
        ...state,
        stepStates: { ...state.stepStates, [stepKey]: stepState },
      };
    },
    // A step operation's whole response ({ data, answers }), kept per operation
    // so a step's list response survives its book response.
    setStepResponse(state, action) {
      const { stepKey, operation, response } = action.payload ?? {};
      return {
        ...state,
        stepResponses: {
          ...state.stepResponses,
          [stepKey]: {
            ...state.stepResponses?.[stepKey],
            [operation]: response,
          },
        },
      };
    },
    setBookingStatus(state, action) {
      return {
        ...state,
        appointment: {
          ...state.appointment,
          bookingStatus: action.payload?.bookingStatus,
        },
      };
    },
    setBookingReference(state, action) {
      return {
        ...state,
        appointment: {
          ...state.appointment,
          bookingReference: action.payload?.bookingReference,
        },
      };
    },
    setBookingOutcome(state, action) {
      return {
        ...state,
        appointment: {
          ...state.appointment,
          bookingOutcome: action.payload?.bookingOutcome,
        },
      };
    },
    setLeadStatus(state, action) {
      return {
        ...state,
        leadStatus: action.payload?.leadStatus,
      };
    },
    setSubscriptionMatchStatus(state, action) {
      return {
        ...state,
        subscriptionMatch: {
          ...state.subscriptionMatch,
          status: action.payload?.subscriptionMatchStatus,
        },
      };
    },
    setSubscriptionMatched(state, action) {
      return {
        ...state,
        subscriptionMatch: {
          ...state.subscriptionMatch,
          isMatched: action.payload?.isSubscriptionMatched,
        },
      };
    },
    resetSubscriptionMatch(state) {
      return {
        ...state,
        subscriptionMatch: {
          status: FetchStatus.NOT_STARTED,
          isMatched: undefined,
        },
      };
    },
    setBookingConfirmable(state, action) {
      return {
        ...state,
        appointment: {
          ...state.appointment,
          isBookingConfirmable: action.payload?.isBookingConfirmable,
        },
      };
    },
    setInactivityPromptOpen(state, action) {
      return {
        ...state,
        isInactivityPromptOpen: action.payload?.isInactivityPromptOpen,
      };
    },
    setInactivityCountdownSeconds(state, action) {
      return {
        ...state,
        inactivityCountdownSeconds: action.payload?.inactivityCountdownSeconds,
      };
    },
    // Set while the journey shows its last notice before handing the customer
    // over to a normal lead, so it can stop its timer and progress bar.
    setFallingThrough(state, action) {
      return {
        ...state,
        isFallingThrough: action.payload?.isFallingThrough,
      };
    },
    setJourneyTimedOut(state, action) {
      return {
        ...state,
        journeyTimedOut: action.payload?.journeyTimedOut,
      };
    },
    setJourneyOutcomeReported(state, action) {
      return {
        ...state,
        isJourneyOutcomeReported: action.payload?.isJourneyOutcomeReported,
      };
    },
  },
});

const { actions, reducer } = clientApiJourney;

export const {
  setLocationsStatus,
  setSolarLoadStatus,
  setSolarVerifyStatus,
  setSolarLocationOverride,
  resetSolar,
  setLocations,
  setSelectedLocation,
  setSlotsStatus,
  setSlots,
  setSelectedSlot,
  initialiseJourney,
  goToNextScreen,
  goToPreviousScreen,
  setStepState,
  setStepResponse,
  setBookingStatus,
  setBookingReference,
  setBookingOutcome,
  setLeadStatus,
  setSubscriptionMatchStatus,
  setSubscriptionMatched,
  resetSubscriptionMatch,
  setBookingConfirmable,
  setInactivityPromptOpen,
  setInactivityCountdownSeconds,
  setJourneyTimedOut,
  setFallingThrough,
  setJourneyOutcomeReported,
} = actions;

export default reducer;
